/**
 * The derivation engine. Given a mode + brand colour(s), produce a full,
 * accessibility-checked design-token set. Pure and deterministic: the same
 * input always yields the same output, in Node or the browser.
 */

import {
  adjustForContrast,
  contrastRatio,
  darken,
  hexToHsl,
  hslToHex,
  lighten,
  mix,
  readableOn,
  rgbToHex,
  hexToRgb,
  relativeLuminance,
  rotateHue,
} from './color.js';

export type BrandMode = 'light' | 'dark';

export interface BrandInput {
  mode: BrandMode;
  primary: string;
  secondary?: string;
}

/** The full derived token set. All values are #rrggbb hex strings. */
export interface DesignTokens {
  mode: BrandMode;
  bg: string; // page background
  surface: string; // cards / panels
  surface2: string; // nested / hover surfaces, inline code
  line: string; // borders, dividers
  text: string; // primary text
  muted: string; // secondary text
  accent: string; // the brand primary, adjusted for legibility on bg
  accentHover: string;
  onAccent: string; // readable text/icon colour ON the accent (black or white)
  secondary: string; // derived from input.secondary or a tasteful shift of accent
  onSecondary: string;
  success: string;
  warning: string;
  danger: string;
  focus: string; // focus ring
}

/** UI element minimum (WCAG 1.4.11). */
const UI_CONTRAST = 3;
/** Text/affordance minimum (WCAG 1.4.3 AA). */
const TEXT_CONTRAST = 4.5;
/** Body text target (WCAG 1.4.6 AAA). */
const BODY_CONTRAST = 7;

/**
 * Tint a neutral toward a hue without polluting it: build the neutral at the
 * brand hue with a tiny saturation so the whole UI reads as "of the brand"
 * while staying a near-neutral dark/light.
 */
function tintedNeutral(hue: number, lightness: number, sat: number): string {
  return hslToHex({ h: hue, s: sat, l: lightness });
}

/** Ensure body text clears AAA (>= 7:1) against bg by pushing it to the extreme. */
function bodyText(bg: string, towardWhite: boolean): string {
  const start = towardWhite ? '#f4f6f5' : '#0c0f0e';
  if (contrastRatio(start, bg) >= BODY_CONTRAST) return start;
  return adjustForContrast(start, bg, BODY_CONTRAST, towardWhite ? 'lighten' : 'darken');
}

/** Muted text: AA (4.5) minimum so secondary copy stays readable. */
function mutedText(bg: string, towardWhite: boolean): string {
  const start = towardWhite ? '#9aa3a0' : '#5c6663';
  if (contrastRatio(start, bg) >= TEXT_CONTRAST) return start;
  return adjustForContrast(start, bg, TEXT_CONTRAST, towardWhite ? 'lighten' : 'darken');
}

/**
 * Resolve the brand-derived accent token. This single colour is used both as a
 * link/icon/border ON the page bg AND as a button FILL, so it must satisfy two
 * constraints at once:
 *   - contrast(accent, bg)        >= 3   (visible as a UI element on the page);
 *   - contrast(onAccent, accent)  >= 4.5 (text on the button fill is legible),
 *     where onAccent is the higher-contrast of black/white.
 *
 * We start from the brand colour, move it toward legibility on bg (lighten in
 * dark mode, darken in light mode), then, if no black/white text reaches AA on
 * it (the classic mid-yellow button), nudge it away from mid-luminance until one
 * does — without ever dropping back below the UI bar against bg.
 */
function resolveAccent(
  brand: string,
  bg: string,
  mode: BrandMode
): { accent: string; onAccent: '#000000' | '#ffffff' } {
  let accent = brand;
  if (contrastRatio(accent, bg) < UI_CONTRAST) {
    accent = adjustForContrast(accent, bg, UI_CONTRAST, mode === 'dark' ? 'lighten' : 'darken');
  }

  // Guarantee a legible on-colour for the fill. If neither black nor white
  // clears AA, shift the fill away from the luminance extreme that's starving
  // contrast, keeping it above the UI bar on bg.
  for (let i = 0; i < 100; i++) {
    const onAccent = readableOn(accent);
    if (contrastRatio(onAccent, accent) >= TEXT_CONTRAST) {
      return { accent, onAccent };
    }
    const lum = relativeLuminance(accent);
    const next = lum > 0.5 ? darken(accent, 0.02) : lighten(accent, 0.02);
    // Don't let the fix break the on-bg UI guarantee.
    if (contrastRatio(next, bg) < UI_CONTRAST) break;
    accent = next;
    const hsl = hexToHsl(accent);
    if (hsl.l <= 0 || hsl.l >= 1) break;
  }
  return { accent, onAccent: readableOn(accent) };
}

function deriveCommon(input: BrandInput, mode: BrandMode) {
  const hue = hexToHsl(input.primary).h;

  let bg: string;
  let surface: string;
  let surface2: string;
  let line: string;
  let textTowardWhite: boolean;
  let success: string;
  let warning: string;
  let danger: string;

  if (mode === 'dark') {
    // Very dark neutrals, subtly tinted toward the brand hue; progressively lighter.
    bg = tintedNeutral(hue, 0.045, 0.1);
    surface = tintedNeutral(hue, 0.075, 0.09);
    surface2 = tintedNeutral(hue, 0.105, 0.08);
    line = tintedNeutral(hue, 0.17, 0.07);
    textTowardWhite = true;
    success = '#34d399';
    warning = '#fbbf24';
    danger = '#f87171';
  } else {
    // Near-white with a faint brand tint; surfaces white-ish.
    bg = tintedNeutral(hue, 0.985, 0.18);
    surface = '#ffffff';
    surface2 = tintedNeutral(hue, 0.965, 0.16);
    line = tintedNeutral(hue, 0.9, 0.12);
    textTowardWhite = false;
    success = '#15803d';
    warning = '#b45309';
    danger = '#b91c1c';
  }

  const text = bodyText(bg, textTowardWhite);
  const muted = mutedText(bg, textTowardWhite);

  const { accent, onAccent } = resolveAccent(input.primary, bg, mode);
  const accentHover = mode === 'dark' ? lighten(accent, 0.1) : darken(accent, 0.1);

  const secondaryBrand = input.secondary ?? rotateHue(input.primary, 150);
  const sec = resolveAccent(secondaryBrand, bg, mode);

  const tokens: DesignTokens = {
    mode,
    bg,
    surface,
    surface2,
    line,
    text,
    muted,
    accent,
    accentHover,
    onAccent,
    secondary: sec.accent,
    onSecondary: sec.onAccent,
    success,
    warning,
    danger,
    focus: mode === 'dark' ? lighten(accent, 0.06) : accent,
  };
  return tokens;
}

/**
 * Derive a full design-token set from a brand input. Deterministic and pure.
 *
 * Contrast guarantees enforced here:
 *  - text vs bg               >= 7:1   (AAA body) in both modes;
 *  - muted vs bg              >= 4.5:1 (AA);
 *  - accent (as text/border) vs bg >= 3:1 (UI element);
 *  - onAccent vs accent       >= 4.5:1 (AA) — buttons are always legible;
 *  - same for secondary / onSecondary.
 */
export function derivePalette(input: BrandInput): DesignTokens {
  // Normalise the brand inputs to canonical hex up front so identical colours
  // written differently (e.g. #FFF vs #ffffff) produce identical tokens.
  const normalized: BrandInput = {
    mode: input.mode,
    primary: rgbToHex(hexToRgb(input.primary)),
    secondary: input.secondary ? rgbToHex(hexToRgb(input.secondary)) : undefined,
  };
  return deriveCommon(normalized, normalized.mode);
}

// Re-export the internal contrast helpers for consumers/tests that want them.
export { contrastRatio, readableOn, mix, lighten, darken, relativeLuminance };
